import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Printer, Plus, Trash2 } from "lucide-react";
import { api, errMsg, openLabels } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { WineFormDialog } from "@/components/WineFormDialog";
import { AddBottlesDialog } from "@/components/AddBottlesDialog";
import { MovementTable } from "@/components/MovementTable";
import { DrinkBadge } from "@/components/DrinkBadge";
import { FileText, MapPin } from "lucide-react";
import { ItemActions } from "@/components/ItemActions";
import { BottleDetailsDialog, bottleSummary } from "@/components/BottleDetailsDialog";
import { BulkMoveDialog } from "@/components/BulkPlace";

const uniq = (arr) => [...new Set(arr.filter((x) => x !== null && x !== undefined && x !== ""))];
const eur = (n) => Number(n).toLocaleString(undefined, { style: "currency", currency: "EUR" });

const STATUS = { consumed: "Consumed", sold: "Sold", lost: "Lost", pending: "Pending", deleted: "Deleted" };

const Info = ({ label, value, id }) => (
  <div>
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E665D]">{label}</p>
    <p className="text-[#F5F0EB] mt-1" data-testid={id}>{value || "—"}</p>
  </div>
);

export default function WineDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [edit, setEdit] = useState(false);
  const [add, setAdd] = useState(false);
  const [details, setDetails] = useState([]);
  const [moving, setMoving] = useState([]);

  const load = useCallback(() => api.get(`/wines/${id}`).then((r) => setData(r.data)).catch(() => navigate("/")), [id, navigate]);
  useEffect(() => {
    load();
  }, [load]);

  if (!data) return null;
  const { wine: w, bottles, boxes, movements } = data;  const sub = `${w.producer || ""} ${w.vintage || "NV"}`.trim();
  const bottleLabel = (b) => ({ barcode: b.barcode, title: w.name, sub, wine: `${w.name} ${w.vintage || "NV"}`, extra: b.box_number ? `Box ${b.box_number}` : "" });
  const boxLabel = (b) => ({ barcode: b.barcode, kind: "box", box_number: b.box_number, title: `Box ${b.box_number}`, sub: `${w.name} · ${sub}`, wine: `${w.vintage || "NV"} ${w.name} · Box ${b.box_number}`, extra: `${b.bottle_count} btl` });
  const allCodes = [...bottles.filter((b) => b.status === "in_cellar").map((b) => b.barcode), ...boxes.map((b) => b.barcode)];
  const count = (s) => bottles.filter((b) => b.status === s).length;
  const inCellar = count("in_cellar");
  const held = bottles.filter((b) => b.status === "in_cellar" || b.status === "pending");
  const prices = uniq(held.map((b) => b.purchase_price)).map(Number).sort((a, b) => a - b);
  const selBottles = bottles.filter((b) => sel.includes(b.barcode));
  const toggle = (code) => setSel(sel.includes(code) ? sel.filter((c) => c !== code) : [...sel, code]);

  const print = (codes) => {
    const labels = [...boxes.filter((b) => codes.includes(b.barcode)).map(boxLabel), ...bottles.filter((b) => codes.includes(b.barcode)).map(bottleLabel)];
    if (!labels.length) return toast.error("Select at least one bottle or box");
    openLabels(labels, navigate);
  };


  const remove = async () => {
    if (!window.confirm(`Delete ${w.name} and all its bottles? You can restore it from Removed.`)) return;
    await api.delete(`/wines/${w.id}`);
    toast.success("Wine deleted — restore it from Removed if needed");
    navigate("/");
  };
  const removeBottles = async () => {
    if (!window.confirm(`Delete ${selBottles.length} bottle${selBottles.length === 1 ? "" : "s"}? You can restore them from Removed.`)) return;
    try {
      await api.post("/bottles/delete", { ids: selBottles.map((b) => b.id) });
      toast.success(`${selBottles.length} bottle${selBottles.length === 1 ? "" : "s"} deleted`);
      setSel([]);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/"));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
      <button onClick={goBack} className="flex items-center gap-2 text-sm text-[#A89F91] hover:text-[#D4AF37] mb-8" data-testid="back-to-inventory"><ArrowLeft className="w-4 h-4" /> Back</button>
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10 rise">
        <div>
          <p className="font-mono text-sm text-[#D4AF37] mb-2" data-testid="wine-detail-vintage">{w.vintage || "NV"}{w.serial_number && <span className="text-[#A89F91]" data-testid="wine-detail-serial-number"> · Serial No. {w.serial_number}</span>}</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#F5F0EB]" data-testid="wine-detail-name">{w.name}</h2>
          <p className="text-[#A89F91] mt-2" data-testid="wine-detail-producer">{w.producer}{w.region ? ` · ${w.region}` : ""}{w.color ? ` · ${w.color}` : ""}</p>
          <BottleTotals inCellar={inCellar} counts={{ Pending: count("pending"), Consumed: count("consumed"), Sold: count("sold"), Lost: count("lost"), Deleted: count("deleted") }} />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setEdit(true)} data-testid="edit-wine-button"><Pencil className="w-4 h-4 mr-2" />Edit</Button>
          <Button variant="outline" onClick={() => setAdd(true)} data-testid="add-bottles-button"><Plus className="w-4 h-4 mr-2" />Add bottles</Button>
          <Button onClick={() => print(sel.length ? sel : allCodes)} className="bg-[#D4AF37] hover:bg-[#E5A93C] text-[#0D0A0B]" data-testid="print-label-batch-button">
            <Printer className="w-4 h-4 mr-2" />{sel.length ? `Print ${sel.length} labels` : "Print all labels"}
          </Button>
          <Button variant="ghost" onClick={remove} className="text-[#E57373]" data-testid="delete-wine-button"><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 rounded-xl border border-[#2E252A] bg-[#161214] p-6 mb-10">
        <Info label="Purchase date" value={uniq(held.map((b) => b.purchase_date)).sort().join(" · ")} id="wine-detail-purchase-date" />
        <Info label="Supplier" value={uniq(held.map((b) => b.supplier)).join(" · ")} id="wine-detail-supplier" />
        <Info label="Purchase price" value={prices.length ? (prices.length > 1 ? `${eur(prices[0])} – ${eur(prices[prices.length - 1])}` : eur(prices[0])) : ""} id="wine-detail-price" />
        <Info label="In cellar" value={`${inCellar} bottle${inCellar === 1 ? "" : "s"}`} id="wine-detail-count" />
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E665D]">Drinking window</p>
          <p className="text-[#F5F0EB] mt-1 flex items-center gap-2 flex-wrap" data-testid="wine-detail-drink-window">
            {w.drink_from || w.drink_until ? `${w.drink_from || "…"} – ${w.drink_until || "…"}` : "—"}
            <DrinkBadge status={data.drink_status} testId="wine-detail-drink-status" />
          </p>
        </div>
        <Info label="Average selling price" value={w.avg_sale_price != null ? eur(w.avg_sale_price) : ""} id="wine-detail-avg-sale-price" />
        <Info label="Bottle size" value={w.bottle_size} id="wine-detail-bottle-size" />
        <Info label="Origin" value={[w.country, w.region, w.sub_region, w.site, w.parcel].filter(Boolean).join(" · ")} id="wine-detail-origin" />
        <Info label="Classification" value={[w.designation, w.classification, w.wine_type, w.sub_type].filter(Boolean).join(" · ")} id="wine-detail-classification" />
        <Info label="Bottle condition" value={uniq(held.map((b) => [b.fill_level && `Fill: ${b.fill_level}`, b.label_condition && `Label: ${b.label_condition}`, b.capsule_condition && `Capsule: ${b.capsule_condition}`, b.condition_other].filter(Boolean).join(", "))).join(" · ")} id="wine-detail-condition" />
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E665D]">Purchase documents</p>
          {uniq(held.map((b) => b.purchase_document?.path)).length ? (
            <p className="mt-1 flex items-center gap-2 text-[#F5F0EB]" data-testid="wine-detail-document"><FileText className="w-4 h-4 shrink-0 text-[#D4AF37]" />{uniq(held.map((b) => b.purchase_document?.path)).length} — open from each bottle</p>
          ) : <p className="text-[#F5F0EB] mt-1">—</p>}
        </div>
        <div className="col-span-2 md:col-span-4"><Info label="Notes" value={w.notes} id="wine-detail-notes" /></div>
      </div>

      {!!boxes.length && <ItemTable title="Boxes" items={boxes} sel={sel} toggle={toggle} onPrint={(b) => print([b.barcode])} kind="box" bottles={bottles} onDone={load} />}
      <ItemTable title="Bottles" items={bottles} sel={sel} toggle={toggle} onPrint={(b) => print([b.barcode])} kind="bottle" bottles={bottles} onDone={load} onDetails={(b) => setDetails([b])}
        extra={!!selBottles.length && (
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => setMoving(selBottles.map((b) => b.barcode))} data-testid="move-selected-button"><MapPin className="w-3.5 h-3.5 mr-2" />Place {selBottles.length} selected</Button>
            <Button size="sm" variant="outline" onClick={() => setDetails(selBottles)} data-testid="edit-selected-details-button"><Pencil className="w-3.5 h-3.5 mr-2" />Edit details of {selBottles.length} selected</Button>
            <Button size="sm" variant="outline" onClick={removeBottles} className="text-[#E57373] border-[#8C2B36]/50" data-testid="delete-selected-bottles-button"><Trash2 className="w-3.5 h-3.5 mr-2" />Delete {selBottles.length} selected</Button>
          </div>
        )} />
      <BulkMoveDialog barcodes={moving} onClose={() => setMoving([])} onDone={() => { setSel([]); load(); }} />
      {!!details.length && <BottleDetailsDialog bottles={details} onClose={() => setDetails([])} onSaved={() => { setSel([]); load(); }} />}

      <h3 className="font-serif text-2xl text-[#D4AF37] mt-12 mb-4">Movements</h3>
      <MovementTable moves={movements} />

      <WineFormDialog open={edit} onOpenChange={setEdit} wine={{ ...w, bottles, boxes }} onSaved={load} inCellar={inCellar} />
      <AddBottlesDialog open={add} onOpenChange={setAdd} wineId={w.id} onSaved={load} />
    </div>
  );
}

const BottleTotals = ({ inCellar, counts }) => (
  <div className="flex items-end gap-5 mt-5 flex-wrap" data-testid="wine-detail-totals">
    <p className="flex items-baseline gap-2"><span className="text-5xl font-semibold tabular-nums text-[#D4AF37]" data-testid="wine-detail-total">{inCellar}</span><span className="text-sm text-[#A89F91]">bottle{inCellar === 1 ? "" : "s"} in the cellar</span></p>
    <p className="font-mono text-xs text-[#6E665D] pb-1.5" data-testid="wine-detail-total-breakdown">
      {Object.entries(counts).filter(([, n]) => n).map(([k, n]) => `${n} ${k.toLowerCase()}`).join(" · ") || ""}
    </p>
  </div>
);

const ItemTable = ({ title, items, sel, toggle, onPrint, kind, bottles, onDone, onDetails, extra }) => (
  <div className="mb-8">
    <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
      <h3 className="font-serif text-2xl text-[#D4AF37]">{title}</h3>
      {extra}
    </div>
    <div className="rounded-xl border border-[#2E252A] overflow-x-auto" data-testid={`${kind}-table`}>
      <table className="w-full text-sm">
        <tbody>
          {items.map((b) => (
            <tr key={b.id} className={`border-t first:border-t-0 border-[#2E252A] ${b.status && b.status !== "in_cellar" ? "opacity-50" : ""}`} data-testid={`${kind}-row-${b.barcode}`}>
              <td className="pl-4 py-3 w-8">{(!b.status || b.status === "in_cellar") && <Checkbox checked={sel.includes(b.barcode)} onCheckedChange={() => toggle(b.barcode)} data-testid={`select-${b.barcode}`} />}</td>
              <td className="px-4 py-3 font-mono text-[#F5F0EB] whitespace-nowrap">{onDetails ? (
                <button onClick={() => (!b.status || b.status === "in_cellar") && toggle(b.barcode)} onDoubleClick={() => onDetails(b)} title="Click to select · double-click to open"
                  className={`select-none rounded px-1.5 -mx-1.5 transition-colors ${sel.includes(b.barcode) ? "bg-[#D4AF37] text-[#0D0A0B]" : "hover:text-[#D4AF37]"}`} data-testid={`barcode-edit-${b.barcode}`}>{b.barcode}</button>
              ) : b.barcode}</td>
              <td className="px-4 py-3 text-[#A89F91]">{kind === "box" ? `Box ${b.box_number} · ${b.bottle_count} bottles` : b.box_number ? `in box ${b.box_number}` : "loose"}</td>
              <td className="px-4 py-3 text-[#F5F0EB] min-w-[140px]" data-testid={`location-${b.barcode}`}>{b.status === "pending" ? "Not yet arrived" : b.status && b.status !== "in_cellar" ? "—" : b.location || "Unassigned"}</td>
              {onDetails && (
                <td className="px-4 py-3 text-xs text-[#A89F91] min-w-[220px]" data-testid={`details-${b.barcode}`}>
                  <button onClick={() => onDetails(b)} className="text-left hover:text-[#D4AF37] transition-colors" data-testid={`edit-details-${b.barcode}`}>
                    {bottleSummary(b) || <span className="text-[#6E665D]">+ purchase & condition</span>}
                    {b.purchase_document && <FileText className="w-3.5 h-3.5 inline ml-1.5 text-[#D4AF37]" />}
                  </button>
                </td>
              )}
              <td className="px-4 py-3 text-right whitespace-nowrap">
                {b.status && b.status !== "in_cellar" && (
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#E5A93C] mr-3" data-testid={`status-${b.barcode}`}>{STATUS[b.status]}</span>
                )}
                <span className="mr-3"><ItemActions item={b} kind={kind} bottles={bottles} onDone={onDone} /></span>
                {(!b.status || b.status === "in_cellar") && <button onClick={() => onPrint(b)} className="text-[#A89F91] hover:text-[#D4AF37]" data-testid={`print-${b.barcode}`} aria-label="Print label"><Printer className="w-4 h-4 inline" /></button>}
              </td>
            </tr>
          ))}
          {!items.length && <tr><td className="px-4 py-8 text-center text-[#6E665D]">None yet.</td></tr>}
        </tbody>
      </table>
    </div>
  </div>
);
